import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const FACE_CHECK_INTERVAL_MS = 25000;

/** Exam-style proctoring: webcam + screen recording (continuous, uploaded once at the end),
 * tab-switch / fullscreen-exit / paste violation tracking, and periodic AI face-presence
 * checks. Fully additive — the interview flow keeps working even if permissions are denied
 * or the browser doesn't support a given API; onTerminate is only called once the violation
 * limit (3) is actually hit. */
export function useProctoring({ sessionId, sessionType, onTerminate }) {
  const [consentGiven, setConsentGiven] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  const [violationCount, setViolationCount] = useState(0);
  const [violationLimit, setViolationLimit] = useState(3);

  const proctoringSessionIdRef = useRef(null);
  const webcamVideoRef = useRef(null);
  const webcamStreamRef = useRef(null);
  const screenStreamRef = useRef(null);
  const webcamRecorderRef = useRef(null);
  const screenRecorderRef = useRef(null);
  const webcamChunksRef = useRef([]);
  const screenChunksRef = useRef([]);
  const faceCheckIntervalRef = useRef(null);
  const finalizedRef = useRef(false);
  const visionCoachEnabledRef = useRef(false);

  const logViolation = useCallback(async (type, message) => {
    if (!proctoringSessionIdRef.current || finalizedRef.current) return;
    try {
      const { data } = await api.post("/proctoring/violation", {
        proctoring_session_id: proctoringSessionIdRef.current, type, message,
      });
      setViolationCount(data.violation_count);
      if (data.should_terminate) {
        toast.error("Interview ended — too many proctoring violations.", { id: "proctor-terminate" });
        finalizeSession();
        onTerminate?.();
      } else {
        toast.warning(`${message} — warning ${data.violation_count}/${data.violation_limit}`, { id: `proctor-${type}` });
      }
    } catch (e) {
      // never let a logging failure break the interview
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onTerminate]);

  const captureAndCheckFace = useCallback(async () => {
    const videoEl = webcamVideoRef.current;
    if (!videoEl || videoEl.readyState < 2 || finalizedRef.current) return;
    try {
      const canvas = document.createElement("canvas");
      canvas.width = 320;
      canvas.height = 240;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height);
      const imageBase64 = canvas.toDataURL("image/jpeg", 0.7).split(",")[1];
      const { data } = await api.post("/proctoring/check-face", {
        proctoring_session_id: proctoringSessionIdRef.current, image_base64: imageBase64,
      });
      if (!data.face_detected) {
        setViolationCount(data.violation_count ?? violationCount + 1);
        toast.warning(`No face detected on camera — warning ${data.violation_count}/${violationLimit}`, { id: "proctor-face" });
        if (data.should_terminate) {
          toast.error("Interview ended — too many proctoring violations.", { id: "proctor-terminate" });
          finalizeSession();
          onTerminate?.();
        }
      }
      // AI Interview OS: AI Vision & Communication Coach (opt-in) — reuse this same frame, no
      // live nudge shown, just silently recorded for the post-interview report card.
      if (visionCoachEnabledRef.current) {
        api.post("/proctoring/vision-coach-check", {
          proctoring_session_id: proctoringSessionIdRef.current, image_base64: imageBase64,
        }).catch(() => {});
      }
    } catch (e) {
      // silent — a failed check just skips this cycle
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [violationCount, violationLimit, onTerminate]);

  const uploadRecording = useCallback(async (chunks, mimeType, kind) => {
    if (!chunks.length || !proctoringSessionIdRef.current) return;
    try {
      const blob = new Blob(chunks, { type: mimeType });
      const form = new FormData();
      form.append("file", blob, `${kind}-${sessionId}.webm`);
      const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/proctoring/session/${proctoringSessionIdRef.current}/attach-recording`, { kind, storage_path: data.path });
    } catch (e) {
      // best-effort — a failed recording upload should never block the interview result
    }
  }, [sessionId]);

  const finalizeSession = useCallback(async () => {
    if (finalizedRef.current) return;
    finalizedRef.current = true;
    if (faceCheckIntervalRef.current) clearInterval(faceCheckIntervalRef.current);
    try { webcamRecorderRef.current?.state !== "inactive" && webcamRecorderRef.current?.stop(); } catch (e) {}
    try { screenRecorderRef.current?.state !== "inactive" && screenRecorderRef.current?.stop(); } catch (e) {}
    webcamStreamRef.current?.getTracks().forEach((t) => t.stop());
    screenStreamRef.current?.getTracks().forEach((t) => t.stop());
    if (document.fullscreenElement) {
      try { await document.exitFullscreen(); } catch (e) {}
    }
    setTimeout(async () => {
      await uploadRecording(webcamChunksRef.current, "video/webm", "webcam");
      await uploadRecording(screenChunksRef.current, "video/webm", "screen");
      if (proctoringSessionIdRef.current) {
        try { await api.post(`/proctoring/session/${proctoringSessionIdRef.current}/complete`); } catch (e) {}
      }
    }, 400);
  }, [uploadRecording]);

  const requestConsent = useCallback(async (visionCoachEnabled = false) => {
    setStarting(true);
    setError(null);
    visionCoachEnabledRef.current = visionCoachEnabled;
    try {
      const webcamStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      const screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true });
      webcamStreamRef.current = webcamStream;
      screenStreamRef.current = screenStream;

      screenStream.getVideoTracks()[0]?.addEventListener("ended", () => {
        logViolation("screen_share_stopped", "Screen sharing was stopped");
      });

      const { data } = await api.post("/proctoring/session/start", { session_id: sessionId, session_type: sessionType, vision_coach_enabled: visionCoachEnabled });
      proctoringSessionIdRef.current = data.proctoring_session_id;
      setViolationLimit(data.violation_limit);
      // React 18 StrictMode double-invokes the mount effect below (mount->cleanup->remount) in dev,
      // which can fire finalizeSession() once before consent is even given, permanently poisoning
      // finalizedRef. Reset it here now that a real session has genuinely started.
      finalizedRef.current = false;

      try {
        const webcamRecorder = new MediaRecorder(webcamStream, { mimeType: "video/webm" });
        webcamRecorder.ondataavailable = (e) => e.data.size > 0 && webcamChunksRef.current.push(e.data);
        webcamRecorder.start(3000);
        webcamRecorderRef.current = webcamRecorder;

        const screenRecorder = new MediaRecorder(screenStream, { mimeType: "video/webm" });
        screenRecorder.ondataavailable = (e) => e.data.size > 0 && screenChunksRef.current.push(e.data);
        screenRecorder.start(3000);
        screenRecorderRef.current = screenRecorder;
      } catch (recErr) {
        // recording is best-effort — proctoring signals (violations/face-check) still work without it
      }

      try {
        await document.documentElement.requestFullscreen();
      } catch (fsErr) {
        // fullscreen may be blocked by the browser — violation tracking continues regardless
      }

      faceCheckIntervalRef.current = setInterval(captureAndCheckFace, FACE_CHECK_INTERVAL_MS);
      setConsentGiven(true);
    } catch (e) {
      setError("Camera and screen-share access are required to start this proctored interview.");
    } finally {
      setStarting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, sessionType, captureAndCheckFace, logViolation]);

  useEffect(() => {
    if (!consentGiven) return;
    const handleVisibility = () => {
      if (document.hidden) logViolation("tab_switch", "Tab switch / minimize detected");
    };
    const handleBlur = () => logViolation("window_blur", "Switched away from the interview window");
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) logViolation("fullscreen_exit", "Exited fullscreen mode");
    };
    const handlePaste = (e) => {
      e.preventDefault();
      logViolation("paste_attempt", "Paste is disabled during the interview");
    };
    const handleContextMenu = (e) => e.preventDefault();

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("blur", handleBlur);
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("paste", handlePaste);
    document.addEventListener("contextmenu", handleContextMenu);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("blur", handleBlur);
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("paste", handlePaste);
      document.removeEventListener("contextmenu", handleContextMenu);
    };
  }, [consentGiven, logViolation]);

  useEffect(() => {
    if (consentGiven && webcamVideoRef.current && webcamStreamRef.current) {
      webcamVideoRef.current.srcObject = webcamStreamRef.current;
    }
  }, [consentGiven]);

  useEffect(() => () => { finalizeSession(); }, [finalizeSession]);

  return { consentGiven, starting, error, violationCount, violationLimit, webcamVideoRef, requestConsent, finalizeSession };
}
